import React, { useEffect } from "react";
import jexcel, { JExcelElement } from "jexcel";

import { IClassForRegister, IClass } from "../../../state";
import { csvEscape, parseCsv } from "../../../libs/csvUtils";
import { ModalContext } from "../../../libs/ModalProvider";
import Confirm from "../../../molecules/Confirm";
import "../../../../node_modules/jexcel/dist/jexcel.css";

import styles from "./ClassSpreadSheet.module.scss";

export interface ClassSpreadSheetProps {
  title: string;
  calling: boolean;
  year: number;
  error: string;
  classes: IClass[];
  save: (classes: IClassForRegister[]) => void;
}
const Fields = {} as { [key: string]: number };
Fields["ID"] = 0;
Fields["学年"] = 1;
Fields["名前"] = 2;
Fields["チェック結果"] = 3;

export const ClassSpreadSheet = (props: ClassSpreadSheetProps) => {
  const ctx = React.useContext(ModalContext);
  const jexcelRef = React.useRef<HTMLDivElement>(null);
  const CSVInputRef = React.useRef<HTMLInputElement>(null);
  const [state, setState] = React.useState({
    sheet: null as null | JExcelElement,
    csvFile: null as null | File,
  });
  const save = props.save;
  const cols = React.useMemo(
    () => [
      { index: 0, title: "ID" },
      { index: 1, title: "学年 ※必須(数値)" },
      { index: 2, title: "クラス名 ※必須" },
    ],
    [],
  );

  const validateDuplicates = React.useCallback((sheet: JExcelElement) => {
    const dataSet = sheet.getData();
    const seen = {} as { [key: string]: boolean };
    dataSet.forEach((d, y) => {
      if (`${d[Fields["学年"]]}${d[Fields["名前"]]}`) {
        const key = `${d[Fields["学年"]]}-${d[Fields["名前"]]}`;
        if (seen[key]) {
          sheet.setValueFromCoords(
            Fields["チェック結果"],
            y,
            `${key}が被っています。\n`,
            false,
          );
        } else {
          sheet.setValueFromCoords(Fields["チェック結果"], y, "", false);
        }
        seen[key] = true;
      }
    });
  }, []);

  const showErrorMessage = React.useCallback(
    (message: string) => {
      if (CSVInputRef.current) {
        CSVInputRef.current.value = "";
      }
      ctx.actions.showModal(
        <Confirm
          content={message}
          closeModal={ctx.actions.closeModal}
          callback={ctx.actions.closeModal}
        />,
      );
    },
    [ctx.actions],
  );

  const exportCSV = React.useCallback(() => {
    const headers = cols.map(col => col.title);
    const exampleRow = ["", "例：1", "例：A組"];
    const bodyRows = props.classes.map(c => [`${c.id}`, `${c.grade}`, c.name]);
    const lines = [
      headers.map(csvEscape).join(","),
      exampleRow.map(csvEscape).join(","),
      ...bodyRows.map(r => r.map(csvEscape).join(",")),
    ].join("\n");
    const BOM = String.fromCharCode(0xfeff);
    const blob = new Blob([BOM + lines + "\n"], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "class_list.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [cols, props.classes]);

  const handleImportCSV = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target?.files ? event.target.files[0] : null;
    if (!file || !state.sheet) {
      return;
    }
    const sheet = state.sheet;
    const reader = new FileReader();
    reader.readAsText(file);
    reader.onload = e => {
      try {
        const csvString = e.target?.result as string;
        const rows = parseCsv(csvString.trim());
        if (rows.length === 0) {
          showErrorMessage("無効なCSV形式です");
          return;
        }
        const headerRow = rows[0];
        const expectedColumns = cols.map(col => col.title);
        const isValidHeader =
          headerRow.length === expectedColumns.length &&
          headerRow.every((col, index) => col === expectedColumns[index]);
        if (!isValidHeader) {
          showErrorMessage(
            "無効な列形式です。適切な列を含む CSV を追加してください",
          );
          return;
        }
        const dataRows = rows
          .slice(1)
          .filter(row => !/^例[:：]/.test(row[1] || ""));
        for (let i = 0; i < dataRows.length; i++) {
          const row = dataRows[i];
          const lineNo = i + 1;
          if (!row[1] || isNaN(Number(row[1])) || Number(row[1]) <= 0) {
            showErrorMessage(
              `${lineNo}行目の「学年」は1以上の数値で入力してください。`,
            );
            return;
          }
          if (!row[2]) {
            showErrorMessage(
              `${lineNo}行目の「クラス名」が入力されていません。`,
            );
            return;
          }
          if (row[0] && isNaN(Number(row[0]))) {
            showErrorMessage(`${lineNo}行目の「ID」は数値で入力してください。`);
            return;
          }
        }
        if (dataRows.length === 0) {
          showErrorMessage("登録できるクラスのデータがありません。");
          return;
        }
        const importedIds = dataRows
          .filter(row => row[0])
          .map(row => Number(row[0]))
          .sort((a, b) => a - b);
        const existingIds = props.classes.map(c => c.id).sort((a, b) => a - b);
        if (importedIds.join(",") !== existingIds.join(",")) {
          showErrorMessage(
            "IDが一致しませんでした。既存行のIDは変更せず、新規追加する行はID列を空欄にしてください。",
          );
          return;
        }
        const sheetData = dataRows.map(row => [
          row[0] ? Number(row[0]) : "",
          Number(row[1]),
          row[2],
          "",
        ]);
        sheet.setData(sheetData as any);
        validateDuplicates(sheet);
        setState(prevState => ({ ...prevState, csvFile: file }));
      } catch (error) {
        showErrorMessage("何か問題が発生しました。もう一度試してください。");
      }
    };
    reader.onerror = () => {
      showErrorMessage("何か問題が発生しました。もう一度試してください。");
    };
  };

  useEffect(() => {
    const jexcelRefCurrent = jexcelRef.current;
    let sheet = null as null | JExcelElement;
    const options = {
      data: props.classes.length
        ? props.classes.map(c => {
            return [c.id, c.grade, c.name, ""];
          })
        : [[]],
      minSpareRows: 2,
      columns: [
        { type: "text" as const, title: "ID", width: 120, readOnly: true },
        {
          type: "numeric" as const,
          title: "学年",
          width: 100,
        },
        { type: "text" as const, title: "名前", width: 120 },
        { type: "text" as const, title: "チェック結果", width: 250 },
      ],
      onchange: (
        _instance: HTMLElement,
        _cell: HTMLTableCellElement,
        x: number,
        _y: number,
        _value: any,
      ) => {
        if (
          sheet &&
          (`${x}` === `${Fields["学年"]}` || `${x}` === `${Fields["名前"]}`)
        ) {
          validateDuplicates(sheet);
        }
      },
    };
    sheet = jexcel(jexcelRefCurrent as any, options as any);
    setState(s => ({
      ...s,
      sheet,
    }));
    return () => {
      jexcelRefCurrent?.remove();
    };
  }, [props.classes, validateDuplicates]);

  const addRow = React.useCallback(() => {
    state.sheet?.insertRow();
  }, [state.sheet]);

  const resetFunc = React.useCallback(() => {
    if (!state.sheet) {
      return;
    }
    if (CSVInputRef.current) {
      CSVInputRef.current.value = "";
    }
    const initialData = props.classes.length
      ? props.classes.map(c => [c.id, c.grade, c.name, ""])
      : [[]];
    state.sheet.setData(initialData as any);
    setState(prevState => ({ ...prevState, csvFile: null }));
  }, [state.sheet, props.classes]);

  const saveFunc = React.useCallback(() => {
    const data = [] as IClassForRegister[];
    if (state.sheet) {
      const dataSet = state.sheet?.getData();
      dataSet.forEach(d => {
        if (d[Fields["学年"]] !== 0 && d[Fields["名前"]] !== "") {
          data.push({
            id: d[Fields["ID"]] as number,
            grade: d[Fields["学年"]] as number,
            name: d[Fields["名前"]] as string,
          });
        }
      });
    }
    save(data);
  }, [save, state.sheet]);

  return (
    <div className={styles.wrapper}>
      <h3>{props.title}</h3>
      <p className={styles.note}>クラスの登録と編集を一括で行えます。</p>
      <div className={styles.actionButtons}>
        <button
          className={`btn btn-outline-danger me-3`}
          disabled={props.calling}
          onClick={() => {
            ctx.actions.showModal(
              <Confirm
                calling={props.calling}
                content={"編集内容をリセットして元の状態に戻しますか？"}
                closeModal={() => {
                  ctx.actions.closeModal();
                }}
                callback={resetFunc}
              />,
            );
          }}
        >
          リセット
        </button>
        <button
          className={`btn btn-outline me-3 ${styles.utilBtn}`}
          type="button"
          onClick={() =>
            ctx.actions.showModal(
              <Confirm
                content={
                  <span>
                    CSVファイルをエクスポートします。
                    <br />
                    <br />
                    クラスを新規追加する際は、エクスポートしたCSVファイル内のクラスデータを残したまま、最下行からデータを追加し「ID」は空欄としてください。2行目は記入例となりますので消さないでください。
                  </span>
                }
                closeModal={() => {
                  ctx.actions.closeModal();
                }}
                callback={exportCSV}
              />,
            )
          }
        >
          {props.classes.length === 0
            ? "インポート用CSVをダウンロード"
            : "CSVエクスポート"}
        </button>
        <button
          type="button"
          className={`btn btn-outline-primary ${styles.importCsv}`}
          onClick={() => CSVInputRef.current?.click()}
        >
          CSVインポート
          <input
            ref={CSVInputRef}
            type="file"
            accept=".csv"
            onChange={handleImportCSV}
          />
        </button>
      </div>
      {state.csvFile && <p className={styles.fileName}>{state.csvFile.name}</p>}
      <div className={styles.excel} ref={jexcelRef} />
      <p className={styles.note}>
        ※「チェック結果」にエラーが表示されている行は登録されませんので、エラーを修正してから登録してください。
      </p>
      <div className={styles.action}>
        <button className="btn btn-primary" onClick={addRow}>
          追加
        </button>
        <button
          className="btn btn-primary"
          onClick={saveFunc}
          disabled={props.calling}
        >
          登録
        </button>
      </div>
    </div>
  );
};

export default ClassSpreadSheet;
